/** A scout's displayed extent, patient mm on its two axes. */
export interface ViewBoxMm {
    inPlaneMin: number;
    inPlaneMax: number;
    zMin: number;
    zMax: number;
}

/**
 * What a scout displays: everything the acquisition covers and everything
 * the region asks for, then tightened by the wheel.
 *
 * The union rather than the acquisition alone, because a field of view may
 * exceed the source — that is how the tool pads — and a limit the user is
 * dragging must not leave the canvas to do it.
 *
 * Zoom is bounded by the region rather than by a free minimum, for the same
 * reason from the other direction: tightening the view can never hide a
 * limit. `zoom <= 1` is the fitted box exactly.
 *
 * `fov` and `z` are the region's edges on this scout's two axes, each null
 * where the region does not constrain that axis.
 */
export function scoutViewBox(
    source: ViewBoxMm,
    fov: readonly [number, number] | null,
    z: readonly [number, number] | null,
    zoom: number
): ViewBoxMm {
    const fitted: ViewBoxMm = {
        inPlaneMin: Math.min(source.inPlaneMin, fov?.[0] ?? Infinity),
        inPlaneMax: Math.max(source.inPlaneMax, fov?.[1] ?? -Infinity),
        zMin: Math.min(source.zMin, z?.[0] ?? Infinity),
        zMax: Math.max(source.zMax, z?.[1] ?? -Infinity),
    };

    if (zoom <= 1) {
        return fitted;
    }

    // Tighten about the fitted centre, then give back whatever the region
    // still needs.
    const shrink = (
        min: number,
        max: number,
        keepMin: number,
        keepMax: number
    ): [number, number] => {
        const centre = (min + max) / 2;
        const half = (max - min) / (2 * zoom);
        return [
            Math.min(centre - half, keepMin),
            Math.max(centre + half, keepMax),
        ];
    };

    const [inPlaneMin, inPlaneMax] = shrink(
        fitted.inPlaneMin,
        fitted.inPlaneMax,
        fov?.[0] ?? fitted.inPlaneMin,
        fov?.[1] ?? fitted.inPlaneMax
    );
    const [zMin, zMax] = shrink(
        fitted.zMin,
        fitted.zMax,
        z?.[0] ?? fitted.zMin,
        z?.[1] ?? fitted.zMax
    );
    return { inPlaneMin, inPlaneMax, zMin, zMax };
}
